import React, { useEffect, useRef } from "react";
import { Input } from "./input";
import { FormField } from "./form-field";

interface OtpInputProps {
  error?: string;
  label?: string;
  length?: number;
  value: string[];
  description?: string;
  onComplete?: (value: string) => void;
  onChange: (value: string[]) => void;
  disabled?: boolean;
}

const isDigit = (value: string) => /^\d$/.test(value);

export const OtpInput = ({
  value,
  error,
  label,
  length = 6,
  description,
  onChange,
  onComplete,
  disabled = false,
}: OtpInputProps) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>(
    Array(length).fill(null),
  );

  // Ensure value array has correct length
  const code = React.useMemo(() => {
    const paddedValue = [...value];
    while (paddedValue.length < length) {
      paddedValue.push("");
    }
    return paddedValue.slice(0, length);
  }, [value, length]);

  const updateCode = (newCode: string[]) => {
    if (disabled) {
      return;
    }

    onChange(newCode);

    // Check if all fields are filled with valid digits
    const isComplete =
      newCode.length === length &&
      newCode.every((digit) => "" !== digit && isDigit(digit));

    if (isComplete && onComplete) {
      onComplete(newCode.join(""));
    }
  };

  const focusInput = (index: number) => {
    if (0 <= index && index < length && inputRefs.current[index]) {
      inputRefs.current[index]?.focus();
    }
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (disabled) {
      return;
    }

    const inputValue = event.target.value;

    // Only allow single digits
    if (1 < inputValue.length) {
      return;
    }

    const newCode = [...code];

    if ("" === inputValue || isDigit(inputValue)) {
      newCode[index] = inputValue;
      updateCode(newCode);

      // Auto-advance to next input if digit was entered
      if ("" !== inputValue && index < length - 1) {
        focusInput(index + 1);
      }
    }
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (disabled) {
      return;
    }

    switch (event.key) {
      case "Backspace":
        if ("" === code[index]) {
          // If current field is empty, go back and clear previous field
          if (0 < index) {
            const newCode = [...code];
            newCode[index - 1] = "";
            updateCode(newCode);
            focusInput(index - 1);
          }
        } else {
          // If current field has content, just clear it
          const newCode = [...code];
          newCode[index] = "";
          updateCode(newCode);
        }
        break;
      case "ArrowLeft":
        event.preventDefault();
        focusInput(index - 1);
        break;
      case "ArrowRight":
        event.preventDefault();
        focusInput(index + 1);
        break;
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }

    event.preventDefault();
    const paste = event.clipboardData?.getData("text");
    if (!paste) {
      return;
    }

    // Extract only digits
    const digits = paste.replace(/\D/g, "");
    if (!digits) {
      return;
    }

    const newCode = Array(length).fill("");
    for (let i = 0; i < Math.min(digits.length, length); i += 1) {
      newCode[i] = digits[i];
    }

    updateCode(newCode);

    // Focus the last filled input or the first empty one
    const lastFilledIndex = Math.min(digits.length - 1, length - 1);
    focusInput(lastFilledIndex);
  };

  // Auto-focus first empty input on mount
  useEffect(() => {
    const firstEmptyIndex = code.findIndex((digit) => "" === digit);
    if (-1 !== firstEmptyIndex) {
      focusInput(firstEmptyIndex);
    } else if (code.every((digit) => "" !== digit)) {
      focusInput(length - 1); // Focus last input if all are filled
    }
  }, []); // Only run on mount

  return (
    <FormField fullWidth label={label} error={error}>
      {description && (
        <span className="text-text-tertiary text-xs">{description}</span>
      )}
      <div className="flex space-x-2" onPaste={handlePaste}>
        {code.map((digit, index) => (
          <Input
            type="text"
            inputMode="numeric"
            key={index}
            maxLength={1}
            value={digit}
            ref={(el: HTMLInputElement | null) => {
              inputRefs.current[index] = el;
            }}
            aria-label={`Digit ${index + 1}`}
            onChange={(event) => handleChange(event, index)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            disabled={disabled}
            className="h-10 text-center sm:h-20"
          />
        ))}
      </div>
    </FormField>
  );
};
